import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowLeft, CalendarClock, Bot, ChevronDown, CheckCircle2,
  Workflow, Boxes, Zap, Gauge, Plug, GraduationCap,
  Ruler, Calculator, DraftingCompass, FileCheck, Network, HardHat,
  LayoutGrid, RefreshCw, Truck, TrendingUp, Headset, BarChart3,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/sections/Footer";
import DemoModal from "@/components/DemoModal";
import WhatsAppAssistant from "@/components/WhatsAppAssistant";
import Seo from "@/components/Seo";
import { getConfig } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const FEATURE_ICONS = {
  gestionale: [Workflow, Boxes, Zap, Gauge, Plug, GraduationCap],
  impianti: [Ruler, Calculator, DraftingCompass, FileCheck, Network, HardHat],
  marketplace: [LayoutGrid, RefreshCw, Truck, TrendingUp, Headset, BarChart3],
};

const HERO_IMAGES = {
  gestionale: "https://images.pexels.com/photos/27141316/pexels-photo-27141316.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  impianti: "https://images.pexels.com/photos/4458205/pexels-photo-4458205.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  marketplace: "https://images.pexels.com/photos/27141307/pexels-photo-27141307.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

const ServicePage = ({ serviceId }) => {
  const { t } = useLanguage();
  const [whatsapp, setWhatsapp] = useState("");
  const [demoOpen, setDemoOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);
  const openDemo = useCallback(() => setDemoOpen(true), []);
  const openAssistant = () => window.dispatchEvent(new Event("xs:open-whatsapp"));

  const sp = t.servicePages;
  const service = sp.services[serviceId];
  const icons = FEATURE_ICONS[serviceId];
  const seoMeta = t.seo[serviceId];

  const faqJsonLd = service.faq
    ? {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: service.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }
    : null;

  useEffect(() => {
    getConfig()
      .then((c) => setWhatsapp(c.whatsapp_number || ""))
      .catch(() => {});
  }, []);

  const waLink = useCallback(
    (text) =>
      `https://wa.me/${(whatsapp || "41782042172").replace(/\D/g, "")}?text=${encodeURIComponent(text)}`,
    [whatsapp]
  );

  return (
    <div className="bg-void text-white font-body min-h-screen" data-testid={`service-page-${serviceId}`}>
      <Seo title={seoMeta.title} description={seoMeta.desc} jsonLd={faqJsonLd} />
      <div className="grain-overlay" />
      <Navbar onOpenDemo={openDemo} waLink={waLink} />
      <main className="pt-16">
        <section className="pt-12 pb-16 lg:pt-16 lg:pb-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <a
                href="/"
                data-testid="service-back-link"
                className="inline-flex items-center gap-2 text-sm text-neon-cyan hover:gap-3 transition-all mb-10"
              >
                <ArrowLeft size={16} /> {sp.back}
              </a>
              <span className="inline-block text-xs font-bold tracking-widest uppercase border border-neon-cyan/40 text-neon-cyan rounded-full px-3 py-1 mb-6">
                {service.chip}
              </span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight max-w-4xl" data-testid="service-title">
                {service.title}
              </h1>
              <p className="mt-6 text-base lg:text-lg text-white/70 max-w-3xl" data-testid="service-subtitle">
                {service.subtitle}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="mt-12 rounded-2xl overflow-hidden relative h-64 sm:h-80 lg:h-96 glass-panel"
            >
              <img
                src={HERO_IMAGES[serviceId]}
                alt={`${service.title} — XS Consulting, Manno (Ticino)`}
                className="w-full h-full object-cover opacity-60"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#08080c] via-transparent to-transparent" />
            </motion.div>
          </div>
        </section>

        <section className="pb-4" data-testid="service-perchi-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6 }}
              className="glass-panel spotlight rounded-2xl p-8 sm:p-10 border-l-2 border-neon-cyan/50"
            >
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mb-4">{sp.perChiTitle}</h2>
              <p className="text-base text-white/75 leading-relaxed max-w-4xl">{service.perChi}</p>
            </motion.div>
          </div>
        </section>

        <section className="py-16 lg:py-20" data-testid="service-features-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
              className="text-xs font-bold tracking-[0.3em] uppercase text-neon-cyan mb-10"
            >
              {sp.whatOverline}
            </motion.p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {service.features.map((f, i) => {
                const Icon = icons[i % icons.length];
                return (
                  <motion.div
                    key={f.title}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.5, delay: (i % 3) * 0.12 }}
                    data-testid={`service-feature-${i}`}
                    className="glass-panel spotlight rounded-2xl p-7 hover:-translate-y-1.5 hover:shadow-[0_0_40px_rgba(0,240,255,0.12)] transition-all duration-500"
                  >
                    <Icon size={24} className="text-neon-cyan mb-4" />
                    <h3 className="font-display text-lg font-bold tracking-tight mb-2">{f.title}</h3>
                    <p className="text-sm text-white/70 leading-relaxed">{f.desc}</p>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-20" data-testid="service-how-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
              className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-12"
            >
              {sp.howTitle}
            </motion.h2>
            <div className="flex flex-col gap-4">
              {t.method.steps.map((s, i) => (
                <motion.div
                  key={s.n}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  data-testid={`service-method-step-${i}`}
                  className="glass-panel spotlight rounded-xl px-6 py-5 flex items-start gap-6 hover:border-neon-cyan/30 transition-colors duration-300"
                >
                  <span className="font-mono text-neon-cyan text-lg font-bold shrink-0 mt-0.5">{s.n}</span>
                  <div>
                    <h3 className="font-display font-bold text-base sm:text-lg">{s.title}</h3>
                    <p className="text-sm text-white/70 mt-1 leading-relaxed">{s.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-4 lg:py-8" data-testid="service-examples-section">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
              className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-10"
            >
              {sp.examplesTitle}
            </motion.h2>
            <div className="flex flex-col gap-3 max-w-4xl">
              {service.examples.map((ex, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  data-testid={`service-example-${i}`}
                  className="flex items-start gap-3 text-white/80 text-sm sm:text-base"
                >
                  <CheckCircle2 size={18} className="text-neon-cyan mt-0.5 shrink-0" /> {ex}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-20">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              data-testid="service-why-panel"
              className="glass-panel spotlight rounded-3xl p-10 sm:p-14 relative overflow-hidden"
            >
              <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-neon-violet/20 blur-[100px] pointer-events-none" />
              <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-neon-cyan/20 blur-[100px] pointer-events-none" />
              <div className="relative max-w-3xl">
                <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-6">
                  {sp.whyTitle}
                </h2>
                <p className="text-base lg:text-lg text-white/75 leading-relaxed">{service.why}</p>
              </div>
            </motion.div>
          </div>
        </section>

        <section className="py-16 lg:py-20" data-testid="service-faq-section">
          <div className="max-w-3xl mx-auto px-6 lg:px-12">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5 }}
              className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-12"
            >
              {sp.faqTitle}
            </motion.h2>
            <div className="flex flex-col gap-4">
              {service.faq.map((f, i) => (
                <motion.div
                  key={f.q}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.4, delay: i * 0.06 }}
                  data-testid={`service-faq-item-${i}`}
                  className="glass-panel spotlight rounded-xl overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    aria-expanded={openFaq === i}
                    data-testid={`service-faq-question-${i}`}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left"
                  >
                    <span className="font-semibold text-sm sm:text-base">{f.q}</span>
                    <ChevronDown size={18} className={`shrink-0 text-neon-cyan transition-transform duration-300 ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5 text-sm text-white/70 leading-relaxed" data-testid={`service-faq-answer-${i}`}>{f.a}</div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl"
            >
              <p className="text-xs font-bold tracking-[0.3em] uppercase text-neon-cyan mb-4">{sp.ctaOverline}</p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight leading-tight">
                {sp.ctaTitle}
              </h2>
              <p className="mt-5 text-base lg:text-lg text-white/70">{sp.ctaText}</p>
              <div className="mt-10 flex flex-wrap gap-4">
                <button
                  onClick={openDemo}
                  data-testid="service-cta-demo"
                  className="flex items-center gap-2 btn-cyber bg-gradient-to-r from-neon-cyan to-neon-violet px-8 py-4 font-semibold text-void hover:opacity-90 hover:-translate-y-0.5 transition-all duration-300"
                >
                  <CalendarClock size={18} /> {sp.ctaPrimary}
                </button>
                <button
                  onClick={openAssistant}
                  data-testid="service-cta-assistant"
                  className="flex items-center gap-2 btn-cyber border border-white/20 px-8 py-4 font-semibold hover:border-neon-cyan/60 hover:text-neon-cyan hover:-translate-y-0.5 transition-all duration-300"
                >
                  <Bot size={18} /> {sp.ctaSecondary}
                </button>
              </div>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer waLink={waLink} />
      <WhatsAppAssistant whatsapp={whatsapp} />
      <DemoModal open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
};

export default ServicePage;
